import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
    getNotifications,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    hideNotification,
} from '../../../../helpers/notification-helper';
import { getNotificationVisual } from '../../../../helpers/notification-ui-helper';

const formatTimeAgo = (dateValue) => {
    if (!dateValue) {
        return 'Ahora';
    }

    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) {
        return 'Ahora';
    }

    const diffMinutes = Math.max(Math.floor((Date.now() - date.getTime()) / (1000 * 60)), 0);
    if (diffMinutes < 1) return 'Ahora';
    if (diffMinutes < 60) return `${diffMinutes} min`;

    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours} h`;

    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} d`;
};

export const NotificationsScreen = () => {
    const navigate = useNavigate();
    const { token } = useSelector((state) => state.auth);

    const [isLoading, setIsLoading] = React.useState(false);
    const [notifications, setNotifications] = React.useState([]);
    const [page, setPage] = React.useState(1);
    const [pagination, setPagination] = React.useState({ page: 1, totalPages: 1, total: 0 });
    const [severityFilter, setSeverityFilter] = React.useState('ALL');

    const fetchNotifications = React.useCallback(async (nextPage = page) => {
        if (!token) {
            return;
        }

        setIsLoading(true);
        const response = await getNotifications(token, { page: nextPage, limit: 20 });
        if (response?.success) {
            const payload = response?.data || {};
            setNotifications(Array.isArray(payload.notifications) ? payload.notifications : []);
            setPagination(payload.pagination || { page: nextPage, totalPages: 1, total: 0 });
        }
        setIsLoading(false);
    }, [token, page]);

    React.useEffect(() => {
        fetchNotifications(page);
    }, [fetchNotifications, page]);

    const handleMarkOneAsRead = async (notificationId) => {
        if (!notificationId || !token) {
            return;
        }

        const response = await markNotificationAsRead(token, notificationId);
        if (!response?.success) {
            return;
        }

        setNotifications((prev) => prev.map((item) => (
            item._id === notificationId
                ? { ...item, isRead: true }
                : item
        )));
    };

    const handleMarkAllAsRead = async () => {
        if (!token) {
            return;
        }

        const response = await markAllNotificationsAsRead(token);
        if (!response?.success) {
            return;
        }

        setNotifications((prev) => prev.map((item) => ({ ...item, isRead: true })));
    };

    const handleHideNotification = async (notificationId) => {
        if (!notificationId || !token) {
            return;
        }

        const response = await hideNotification(token, notificationId);
        if (!response?.success) {
            return;
        }

        setNotifications((prev) => prev.filter((item) => item._id !== notificationId));
    };

    const handleOpenTicket = async (notification) => {
        if (!notification) {
            return;
        }

        if (!notification.isRead) {
            await handleMarkOneAsRead(notification._id);
        }

        const ticketId = notification?.ticket?._id || notification?.metadata?.ticketId || '';
        if (ticketId) {
            navigate(`/tasks/detail/${ticketId}`);
        }
    };

    const unreadCount = notifications.filter((item) => !item.isRead).length;

    const filteredNotifications = React.useMemo(() => {
        if (severityFilter === 'ALL') {
            return notifications;
        }

        return notifications.filter((notification) => {
            const tone = getNotificationVisual(notification.eventType).tone;
            const isCritical = tone === 'danger' || tone === 'warning';

            if (severityFilter === 'CRITICAL') {
                return isCritical;
            }

            if (severityFilter === 'INFORMATIONAL') {
                return !isCritical;
            }

            return true;
        });
    }, [notifications, severityFilter]);

    const criticalCount = React.useMemo(
        () => notifications.filter((notification) => {
            const tone = getNotificationVisual(notification.eventType).tone;
            return tone === 'danger' || tone === 'warning';
        }).length,
        [notifications],
    );

    const informationalCount = Math.max(notifications.length - criticalCount, 0);

    return (
        <div className="row">
            <div className="col-12">
                <div className="card stretch stretch-full card__no-border">
                    <div className="card-body pt-3">
                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                            <div>
                                <h5 className="mb-0">Notificaciones</h5>
                                <small className="text-muted">{unreadCount} sin leer · {pagination.total || 0} totales</small>
                            </div>
                            <button
                                type="button"
                                className="btn btn-sm btn-light-brand"
                                onClick={handleMarkAllAsRead}
                                disabled={unreadCount === 0}
                            >
                                Marcar todas como leídas
                            </button>
                        </div>

                        <div className="notification-severity-filters mb-3">
                            <button
                                type="button"
                                className={`btn btn-sm ${severityFilter === 'ALL' ? 'btn-primary' : 'btn-light'}`}
                                onClick={() => setSeverityFilter('ALL')}
                            >
                                Todas ({notifications.length})
                            </button>
                            <button
                                type="button"
                                className={`btn btn-sm ${severityFilter === 'CRITICAL' ? 'btn-danger' : 'btn-light'}`}
                                onClick={() => setSeverityFilter('CRITICAL')}
                            >
                                Críticas ({criticalCount})
                            </button>
                            <button
                                type="button"
                                className={`btn btn-sm ${severityFilter === 'INFORMATIONAL' ? 'btn-info text-white' : 'btn-light'}`}
                                onClick={() => setSeverityFilter('INFORMATIONAL')}
                            >
                                Informativas ({informationalCount})
                            </button>
                        </div>

                        {isLoading && (
                            <div className="text-muted py-4">Cargando notificaciones...</div>
                        )}

                        {!isLoading && filteredNotifications.length === 0 && (
                            <div className="notification-empty-state text-center py-5 border rounded-3">
                                <div className="notification-empty-state__icon mb-2">
                                    <i className="feather-check-circle"></i>
                                </div>
                                <h6 className="mb-1">Todo al día</h6>
                                <p className="text-muted mb-0">
                                    {severityFilter === 'ALL'
                                        ? 'No tienes notificaciones pendientes por revisar.'
                                        : 'No hay notificaciones para este filtro.'}
                                </p>
                            </div>
                        )}

                        {!isLoading && filteredNotifications.length > 0 && (
                            <div className="list-group notification-list-screen">
                                {filteredNotifications.map((notification) => (
                                    <div
                                        key={notification._id}
                                        className={`list-group-item notification-list-screen__item notification-tone notification-tone--${getNotificationVisual(notification.eventType).tone} ${notification.isRead ? 'is-read' : 'is-unread'}`}
                                    >
                                        <div className="d-flex justify-content-between align-items-start gap-2">
                                            <div className="notification-tone__icon mt-1">
                                                <i className={getNotificationVisual(notification.eventType).icon}></i>
                                            </div>
                                            <button
                                                type="button"
                                                className="notification-list-screen__content border-0 bg-transparent text-start p-0 notification-clickable"
                                                onClick={() => handleOpenTicket(notification)}
                                            >
                                                <div>
                                                    <span className={`badge bg-light text-${getNotificationVisual(notification.eventType).tone === 'neutral' ? 'secondary' : getNotificationVisual(notification.eventType).tone}`}>{getNotificationVisual(notification.eventType).label}</span>
                                                </div>
                                                <div className="fw-semibold text-dark">{notification.title}</div>
                                                <div className="text-muted">{notification.message}</div>
                                                <div className="small text-muted mt-1">{formatTimeAgo(notification.createdDate)}</div>
                                            </button>
                                            <div className="d-flex align-items-center gap-1">
                                                {!notification.isRead && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-light"
                                                        title="Marcar como leída"
                                                        onClick={() => handleMarkOneAsRead(notification._id)}
                                                    >
                                                        <i className="feather-check"></i>
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-light text-danger"
                                                    title="Ocultar"
                                                    onClick={() => handleHideNotification(notification._id)}
                                                >
                                                    <i className="feather-x"></i>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {!isLoading && pagination.totalPages > 1 && (
                            <div className="d-flex justify-content-end gap-2 mt-3">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-light"
                                    onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                                    disabled={page <= 1}
                                >
                                    Anterior
                                </button>
                                <span className="align-self-center text-muted small">
                                    Página {page} de {pagination.totalPages}
                                </span>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-light"
                                    onClick={() => setPage((prev) => Math.min(prev + 1, pagination.totalPages))}
                                    disabled={page >= pagination.totalPages}
                                >
                                    Siguiente
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
